import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import careparrvaOutputService from '../../services/careparrvaOutputService';
import { showToast } from '../../components/toast';
import './CareparrvaReportDetail.css';

/**
 * Formats values into clean percentages or readable text
 */
const formatPercent = (val) => {
  if (val === null || val === undefined || val === '' || val === 'NA' || val === 'null') {
    return '-';
  }
  const num = parseFloat(val);
  if (isNaN(num)) return String(val);

  return num.toFixed(2);
};

const formatValue = (val) => {
  if (val === null || val === undefined || val === '' || val === 'NA' || val === 'null') {
    return '-';
  }
  return String(val);
};

const CareparrvaReportDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    if (id) {
      loadReport();
    }
  }, [id]);

  const loadReport = async (forceFetch = false) => {
    // 1. If passed via navigation state, check if it has complete table data
    if (!forceFetch && location.state?.report && String(location.state.report.id) === String(id)) {
      const stateReport = location.state.report;
      let hasTables = false;
      try {
        if (stateReport.data_points) {
          const dp =
            typeof stateReport.data_points === 'object'
              ? stateReport.data_points
              : JSON.parse(stateReport.data_points);
          if (
            dp?.summary_data_points ||
            dp?.detailed_data_points ||
            dp?.equity_intraday ||
            dp?.portfolio_list_table
          ) {
            hasTables = true;
          }
        }
      } catch (e) {}

      // If state report has actual tables and is not pending, use immediately
      if (hasTables && stateReport.status !== 'PENDING') {
        setReport(stateReport);
        setLoading(false);
        return;
      }
    }

    try {
      setLoading(true);

      // 2. Try direct getReportById (backend will auto-sync if pending)
      try {
        const res = await careparrvaOutputService.getReportById(id);
        if (res && (res.success || res.data)) {
          setReport(res.data || res);
          return;
        }
      } catch (directErr) {
        // Fallback to getReports if direct endpoint is not available
      }

      // 3. Resilient fallback: lookup in getReports list
      const listRes = await careparrvaOutputService.getReports({ page: 1, limit: 100 });
      const list = Array.isArray(listRes?.data)
        ? listRes.data
        : Array.isArray(listRes)
        ? listRes
        : [];
      const found = list.find((r) => String(r.id) === String(id));
      if (found) {
        setReport(found);
      } else {
        showToast.error('Report not found');
        navigate('/research/careparrva-output');
      }
    } catch (err) {
      console.error('Error fetching report details:', err);
      showToast.error('Failed to load report details');
      navigate('/research/careparrva-output');
    } finally {
      setLoading(false);
    }
  };

  // Sync / refresh report status directly from CareEdge PaRRVA API
  const handleSync = async () => {
    try {
      setSyncing(true);
      const res = await careparrvaOutputService.syncReportStatus(id);
      if (res && (res.report || res.data || res.success)) {
        const updated = res.report || res.data;
        if (updated) {
          setReport(updated);
        } else {
          await loadReport(true);
        }
        showToast.success('Report status checked and refreshed!');
      } else {
        showToast.info(res?.message || 'Report is still being generated by CareEdge PaRRVA');
      }
    } catch (err) {
      console.error('Error syncing report status:', err);
      showToast.error(err?.response?.data?.message || err?.message || 'Failed to sync status');
    } finally {
      setSyncing(false);
    }
  };

  // Safely parse data_points JSON
  const parsed = useMemo(() => {
    if (!report?.data_points) return null;
    if (typeof report.data_points === 'object') return report.data_points;
    try {
      return JSON.parse(report.data_points);
    } catch (e) {
      console.error('Could not parse report data points JSON:', e);
      return null;
    }
  }, [report]);

  // Download raw JSON
  const handleDownloadJson = () => {
    if (!report?.data_points) return;
    try {
      const content =
        typeof report.data_points === 'string'
          ? report.data_points
          : JSON.stringify(report.data_points, null, 2);
      const blob = new Blob([content], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `careparrva_${report.report_type.toLowerCase()}_${report.id}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      showToast.success('JSON file downloaded');
    } catch (err) {
      console.error('Error downloading json:', err);
      showToast.error('Could not download JSON');
    }
  };

  if (loading) {
    return (
      <div id="page-container" className="page-header-fixed page-sidebar-fixed fade in">
        <div id="content" className="p-3 d-flex align-items-center justify-content-center" style={{ minHeight: '60vh', background: '#ffffff' }}>
          <div className="text-center">
            <div className="spinner-border text-primary" role="status"></div>
            <div className="mt-3 text-muted font-weight-bold">Loading PaRRVA Verification Report #{id}...</div>
          </div>
        </div>
      </div>
    );
  }

  if (!report || !parsed) {
    return (
      <div id="page-container" className="page-header-fixed page-sidebar-fixed fade in">
        <div id="content" className="p-3" style={{ background: '#ffffff' }}>
          <div className="alert alert-warning text-center">
            Report data could not be loaded or parsed.
            <div className="mt-3">
              <Link to="/research/careparrva-output" className="btn btn-sm btn-primary">
                Back to Reports
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // 1. Header & Organization Information
  const headerData =
    parsed?.detailed_data_points?.header_data_points ||
    parsed?.summary_data_points?.summary?.header_data_points ||
    parsed?.summary_data_points?.header_data_points ||
    parsed?.header_data_points ||
    null;

  // Period label formatter
  const periodLabelMap = {
    '1_month': '1 Month',
    '3_months': '3 Months',
    '6_months': '6 Months',
    '1_year': '1 Year',
    '3_years': '3 Years',
    '5_years': '5 Years',
    'since_inception': 'Since Inception',
  };

  const formatPeriodHeader = (key) => {
    if (periodLabelMap[key]) return periodLabelMap[key];
    return key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  };

  // 2. Summary Sections
  const summaryObj = parsed?.summary_data_points?.summary || {};

  // A. Equity Intraday Recommendations (PDF Page 1 Table 1)
  const intradaySummary = summaryObj?.equity_intraday || summaryObj?.intraday || null;

  // B. Single Stock Recommendations (PDF Page 2 Tables 2 & 3)
  const singleStockSummary =
    summaryObj?.single_stock ||
    summaryObj?.singlestock ||
    summaryObj?.technical ||
    null;

  // C. Derivative Summary (PDF Page 2 Tables 4 & 5)
  const derivativeSummary = summaryObj?.derivative || summaryObj?.derivatives || null;

  // D. Portfolio Recommendations (PDF Pages 3-6 Tables 6 & 7)
  const portfolioListTable = summaryObj?.portfolio_list_table || null;
  const portfolioList = portfolioListTable?.portfolios || [];
  const portfolioAggregate = portfolioListTable?.aggregate || null;

  // Dynamically determine which period columns have data in portfolio recommendations
  const preferredPeriods = ['1_month', '3_months', '6_months', '1_year', '3_years', '5_years', 'since_inception'];
  const activePeriods = preferredPeriods.filter((k) =>
    portfolioList.some((p) => {
      const val = p?.return_in_percent?.[k];
      return val !== undefined && val !== null && val !== '' && val !== 'NA' && val !== 'null';
    })
  );
  const dynamicPeriods = activePeriods.length > 0 ? activePeriods : ['1_month', 'since_inception'];

  // 3. Detailed Report Sections (PDF Pages 8-58)
  const detailedReport = parsed?.detailed_data_points || null;
  const detailedSectionB = detailedReport?.section_b_detailed_report || null;
  const detailedPortfolios = detailedSectionB?.portfolios || [];

  const jobId = parsed?.job_id || report?.request_id || null;
  const isPendingJob = Boolean(
    report?.status === 'PENDING' ||
      (parsed?.job_id &&
        !intradaySummary &&
        !singleStockSummary &&
        !derivativeSummary &&
        (!portfolioList || portfolioList.length === 0) &&
        (!detailedPortfolios || detailedPortfolios.length === 0)),
  );

  const hasAnyTable = Boolean(
    intradaySummary ||
      singleStockSummary ||
      derivativeSummary ||
      (portfolioList && portfolioList.length > 0) ||
      (detailedPortfolios && detailedPortfolios.length > 0),
  );

  return (
    <div id="page-container" className="page-header-fixed page-sidebar-fixed fade in">
      <div id="content" className="p-3" style={{ background: '#ffffff', minHeight: '100vh' }}>
        {/* Top Toolbar: Back, Sync Status, and Download JSON Buttons */}
        <div className="parrva-toolbar">
          <div>
            <Link to="/research/careparrva-output" className="btn btn-sm btn-default border">
              ← Back to Reports
            </Link>
          </div>

          <div className="d-flex align-items-center" style={{ gap: '8px' }}>
            <button
              type="button"
              className="btn btn-sm btn-warning text-dark font-weight-bold"
              onClick={handleSync}
              disabled={syncing}
              title="Sync latest status from CareEdge PaRRVA API"
            >
              <i className={`fa fa-sync-alt mr-1 ${syncing ? 'fa-spin' : ''}`}></i>
              {syncing ? 'Syncing...' : 'Sync Status'}
            </button>
            <button
              type="button"
              className="btn btn-sm btn-primary"
              onClick={handleDownloadJson}
            >
              Download JSON
            </button>
          </div>
        </div>

        {/* ============================================================== */}
        {/* REPORT CONTENT (CLEAN CRM CARD, FULL WIDTH)                   */}
        {/* ============================================================== */}
        <div className="parrva-document-paper" id="parrva-print-area">
          {/* Pending Async Job Notification Banner */}
          {isPendingJob && (
            <div
              className="alert alert-warning p-4 mb-4"
              style={{ borderRadius: '6px', border: '1px solid #ffeeba' }}
            >
              <div
                className="d-flex align-items-center justify-content-between flex-wrap"
                style={{ gap: '15px' }}
              >
                <div>
                  <h5 className="font-weight-bold mb-1" style={{ color: '#856404' }}>
                    <i className="fa fa-clock mr-2"></i> Report Generation in Progress
                  </h5>
                  <p className="mb-0 text-muted" style={{ fontSize: '13px' }}>
                    This historical report was submitted to CareEdge PaRRVA and is currently being processed asynchronously.
                    {jobId && (
                      <span>
                        {' '}
                        (Job ID: <code>{jobId}</code>)
                      </span>
                    )}
                  </p>
                </div>
                <button
                  type="button"
                  className="btn btn-warning px-3 font-weight-bold text-dark"
                  onClick={handleSync}
                  disabled={syncing}
                >
                  <i className={`fa fa-sync-alt mr-2 ${syncing ? 'fa-spin' : ''}`}></i>
                  {syncing ? 'Checking CareEdge...' : 'Check Status & Sync Now'}
                </button>
              </div>
            </div>
          )}

          {/* Fallback if no tables returned and not pending */}
          {!hasAnyTable && !isPendingJob && (
            <div className="text-center p-5 border rounded bg-white">
              <i className="fa fa-info-circle fa-3x text-muted mb-3"></i>
              <h5 className="font-weight-bold mb-2">No Verification Data Points Available</h5>
              <p className="text-muted mb-3">
                No recommendation summary or detail tables were returned for this report.
              </p>
              <div className="d-flex justify-content-center" style={{ gap: '10px' }}>
                <button
                  type="button"
                  className="btn btn-sm btn-warning text-dark font-weight-bold"
                  onClick={handleSync}
                  disabled={syncing}
                >
                  <i className={`fa fa-sync-alt mr-1 ${syncing ? 'fa-spin' : ''}`}></i>
                  Check & Sync Status
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-primary"
                  onClick={handleDownloadJson}
                >
                  Download JSON
                </button>
              </div>
            </div>
          )}

          {/* ============================================================ */}
          {/* EQUITY INTRADAY RECOMMENDATIONS                              */}
          {/* ============================================================ */}
          {intradaySummary && (
          <div>
            <h2 className="parrva-section-heading">Equity Intraday Recommendations</h2>

            <table className="parrva-report-table">
              <thead>
                <tr>
                  <th className="text-center">Net Avg.<br />P&L (%)</th>
                  <th className="text-center">Positive<br />(Count)</th>
                  <th className="text-center">Avg.<br />Profitability (%)</th>
                  <th className="text-center">Negative<br />(Count)</th>
                  <th className="text-center">Avg.<br />Loss (%)</th>
                  <th className="text-center">Success<br />Ratio (%)</th>
                  <th className="text-center">Reward to Risk<br />Ratio</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="text-center font-weight-bold">{formatPercent(intradaySummary.avg_net_profit_loss)}</td>
                  <td className="text-center">{formatValue(intradaySummary.positive)}</td>
                  <td className="text-center">{formatPercent(intradaySummary.avg_profitability)}</td>
                  <td className="text-center text-danger">{formatValue(intradaySummary.negative)}</td>
                  <td className="text-center text-danger font-weight-bold">{formatPercent(intradaySummary.avg_loss)}</td>
                  <td className="text-center font-weight-bold">{formatPercent(intradaySummary.success_ratio)}</td>
                  <td className="text-center font-weight-bold">{formatValue(intradaySummary.reward_to_risk_ratio)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}

        {/* ============================================================ */}
        {/* SINGLE STOCK (DELIVERY BASED)                                */}
        {/* ============================================================ */}
        {singleStockSummary && (
          <div>
            <h2 className="parrva-section-heading">Single Stock (Delivery based)</h2>
            <h3 className="parrva-section-subheading">Technical Recommendations</h3>

            {singleStockSummary.closed && (
              <>
                <table className="parrva-report-table">
                  <thead>
                    <tr>
                      <th className="text-center">Net Avg. P&L (%)</th>
                      <th className="text-center">Positive (Count)</th>
                      <th className="text-center">Avg. Profitability (%)</th>
                      <th className="text-center">Negative (Count)</th>
                      <th className="text-center">Avg. Loss (%)</th>
                      <th className="text-center">Success Ratio (%)</th>
                      <th className="text-center">Reward to Risk Ratio</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="text-center font-weight-bold">{formatPercent(singleStockSummary.closed.avg_net_profit_loss)}</td>
                      <td className="text-center">{formatValue(singleStockSummary.closed.positive)}</td>
                      <td className="text-center">{formatPercent(singleStockSummary.closed.avg_profitability)}</td>
                      <td className="text-center text-danger">{formatValue(singleStockSummary.closed.negative)}</td>
                      <td className="text-center text-danger">{formatPercent(singleStockSummary.closed.avg_loss)}</td>
                      <td className="text-center font-weight-bold">{formatPercent(singleStockSummary.closed.success_ratio)}</td>
                      <td className="text-center">{formatValue(singleStockSummary.closed.reward_to_risk_ratio)}</td>
                    </tr>
                  </tbody>
                </table>
              </>
            )}

            {singleStockSummary.open && (
              <>
                <table className="parrva-report-table">
                  <thead>
                    <tr>
                      <th className="text-center">Net Avg. P&L (%)</th>
                      <th className="text-center">Positive (Count)</th>
                      <th className="text-center">Unrealised Avg. Gain (%)</th>
                      <th className="text-center">Negative (Count)</th>
                      <th className="text-center">Unrealised Avg. Loss (%)</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="text-center font-weight-bold">{formatPercent(singleStockSummary.open.avg_net_profit_loss)}</td>
                      <td className="text-center">{formatValue(singleStockSummary.open.positive)}</td>
                      <td className="text-center">{formatPercent(singleStockSummary.open.unrealised_avg_gain)}</td>
                      <td className="text-center text-danger">{formatValue(singleStockSummary.open.negative)}</td>
                      <td className="text-center text-danger font-weight-bold">{formatPercent(singleStockSummary.open.unrealised_avg_loss)}</td>
                    </tr>
                  </tbody>
                </table>
              </>
            )}
          </div>
        )}

        {/* ============================================================ */}
        {/* 3. DERIVATIVE SUMMARY REPORT (PDF Page 2)                    */}
        {/* ============================================================ */}
        {derivativeSummary && (
          <div>
            <h2 className="parrva-section-heading">Derivative Summary Report</h2>

            {Array.isArray(derivativeSummary.closed) && (
              <>
                <table className="parrva-report-table">
                  <thead>
                    <tr>
                      <th>Category</th>
                      <th className="text-center">Total (Count)</th>
                      <th className="text-center">Net Avg. P&L (%)</th>
                      <th className="text-center">Positive (Count)</th>
                      <th className="text-center">Avg. Profitability (%)</th>
                      <th className="text-center">Negative (Count)</th>
                      <th className="text-center">Avg. Loss (%)</th>
                      <th className="text-center">Success Ratio (%)</th>
                      <th className="text-center">Reward to Risk Ratio</th>
                    </tr>
                  </thead>
                  <tbody>
                    {derivativeSummary.closed.map((row, rIdx) => (
                      <tr key={rIdx}>
                        <td className="font-weight-bold">{row.category}</td>
                        <td className="text-center">{row.total}</td>
                        <td className="text-center">{formatPercent(row.avg_net_profit_loss)}</td>
                        <td className="text-center">{row.positive}</td>
                        <td className="text-center">{formatPercent(row.avg_profitability)}</td>
                        <td className="text-center text-danger">{row.negative}</td>
                        <td className="text-center text-danger">{formatPercent(row.avg_loss)}</td>
                        <td className="text-center font-weight-bold">{formatPercent(row.success_ratio)}</td>
                        <td className="text-center">{row.reward_to_risk_ratio}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}
          </div>
        )}

        {/* ============================================================ */}
        {/* PORTFOLIO (BASKET OF SECURITIES)                             */}
        {/* ============================================================ */}
        {portfolioList.length > 0 && (
          <div>
            <h2 className="parrva-section-heading">Portfolio (Basket of securities)</h2>

            {/* Exact PDF Layout: 3 Rows Per Portfolio with rowSpan="3" */}
            <table className="parrva-report-table">
              <thead>
                <tr>
                  <th style={{ width: '220px' }}>Portfolio Name</th>
                  <th className="text-center" style={{ width: '130px' }}>Date of<br />Inception on<br />PaRRVA</th>
                  <th style={{ width: '220px' }}>Parameter</th>
                  {dynamicPeriods.map((pKey) => (
                    <th key={pKey} className="text-center" style={{ minWidth: '100px' }}>
                      {formatPeriodHeader(pKey)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {portfolioList.map((item, pIdx) => {
                  const retObj = item.return_in_percent || {};
                  const decObj = item.max_decline_from_peak_percent || {};
                  const sharpeObj = item.risk_adjusted_return_sharpe_ratio || {};

                  return (
                    <React.Fragment key={pIdx}>
                      {/* Row 1: Return in % */}
                      <tr>
                        <td rowSpan="3" className="font-weight-bold align-middle" style={{ verticalAlign: 'middle', backgroundColor: '#fcfdfd' }}>
                          {item.portfolio_name}
                        </td>
                        <td rowSpan="3" className="text-center align-middle" style={{ verticalAlign: 'middle', backgroundColor: '#fcfdfd', fontSize: '12px' }}>
                          {item.date_of_inception_on_parrva || '-'}
                        </td>
                        <td>Return in %</td>
                        {dynamicPeriods.map((pKey) => (
                          <td key={pKey} className="text-right font-weight-bold">
                            {formatPercent(retObj[pKey])}
                          </td>
                        ))}
                      </tr>

                      {/* Row 2: Max Decline from Peak % */}
                      <tr>
                        <td>Max Decline from Peak %</td>
                        {dynamicPeriods.map((pKey) => (
                          <td key={pKey} className="text-right text-danger font-weight-bold">
                            {formatPercent(decObj[pKey])}
                          </td>
                        ))}
                      </tr>

                      {/* Row 3: Risk Adjusted Return (Sharpe ratio) */}
                      <tr>
                        <td>Risk Adjusted Return (Sharpe ratio)</td>
                        {dynamicPeriods.map((pKey) => (
                          <td key={pKey} className="text-right">
                            {formatPercent(sharpeObj[pKey])}
                          </td>
                        ))}
                      </tr>
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>

            {/* Min to Max Return & Risk Parameter Across Portfolios */}
            {portfolioAggregate && (
              <div className="mt-4">
                <table className="parrva-report-table">
                  <thead>
                    <tr>
                      <th style={{ width: '450px' }}>Parameter</th>
                      {dynamicPeriods.map((pKey) => (
                        <th key={pKey} className="text-center" style={{ minWidth: '120px' }}>
                          {formatPeriodHeader(pKey)}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="font-weight-bold">Range of Return in % across portfolios</td>
                      {dynamicPeriods.map((pKey) => (
                        <td key={pKey} className="text-right font-weight-bold">
                          {portfolioAggregate.return_in_percent_range?.[pKey] || '-'}
                        </td>
                      ))}
                    </tr>
                    <tr>
                      <td className="font-weight-bold">Range of Max Decline from Peak % across portfolios</td>
                      {dynamicPeriods.map((pKey) => (
                        <td key={pKey} className="text-right font-weight-bold text-danger">
                          {portfolioAggregate.max_decline_from_peak_percent_range?.[pKey] || '-'}
                        </td>
                      ))}
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* ============================================================ */}
        {/* DETAILED REPORT SECTION                                      */}
        {/* ============================================================ */}
        {detailedPortfolios.length > 0 && (
          <div>
            <h2 className="parrva-section-heading mt-4">Detailed Report</h2>

            {detailedPortfolios.map((dp, dIdx) => {
              const snap = dp.portfolio_snapshot || {};
              const riskParams = dp.return_risk_parameters || {};
              const highLow = dp.fifty_two_week_high_low;
              const chart = dp.historical_index_movement_chart?.[0];

              return (
                <div key={dIdx} className="mb-5 pb-3 border-bottom">
                  <h3 className="parrva-section-subheading mb-3">
                    {dIdx + 1}. {snap.strategy_name || `Strategy ${dIdx + 1}`}
                  </h3>

                  {/* Portfolio Snapshot Table */}
                  <table className="parrva-report-table mb-3">
                    <thead>
                      <tr>
                        <th style={{ width: '50%' }}>Parameter</th>
                        <th style={{ width: '50%' }}>Value</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td className="font-weight-bold">Strategy Name</td>
                        <td>{snap.strategy_name || '-'}</td>
                      </tr>
                      <tr>
                        <td className="font-weight-bold">Asset Class</td>
                        <td className="text-capitalize">{snap.asset_class || 'Equity'}</td>
                      </tr>
                      <tr>
                        <td className="font-weight-bold">Date of inception on PaRRVA</td>
                        <td>{snap.date_of_inception_on_parrva || '-'}</td>
                      </tr>
                      <tr>
                        <td className="font-weight-bold">No. of Stocks</td>
                        <td>{snap.no_of_stocks ?? '-'}</td>
                      </tr>
                      <tr>
                        <td className="font-weight-bold">No. Of Rebalances/Modifications</td>
                        <td>{snap.no_of_rebalances_modifications ?? 0}</td>
                      </tr>
                      <tr>
                        <td className="font-weight-bold">Corporate Action</td>
                        <td>{snap.corporate_action || '-'}</td>
                      </tr>
                    </tbody>
                  </table>

                  {/* Latest Benchmark Movement / Index (PDF Page 13 Graph 4) */}
                  {chart && (
                    <div className="p-2 mb-3 bg-light border text-muted" style={{ fontSize: '11.5px' }}>
                      <strong>Historical Index Movement Benchmark:</strong> Index Value is <strong>{parseFloat(chart.index_value).toFixed(2)}</strong> as of <strong>{chart.date}</strong>.
                    </div>
                  )}

                  {/* Portfolio Performance - Returns and Risk Parameters */}
                  {(() => {
                    const preferred = ['1_month', '3_months', '6_months', '1_year', '3_years', '5_years', 'since_inception'];
                    const stratPeriods = preferred.filter((k) => {
                      const val = riskParams.return_in_percent?.[k];
                      return val !== undefined && val !== null && val !== '' && val !== 'NA' && val !== 'null';
                    });
                    const activePeriods = stratPeriods.length > 0 ? stratPeriods : ['1_month', 'since_inception'];

                    return (
                      <>
                        <table className="parrva-report-table mb-3">
                          <thead>
                            <tr>
                              <th style={{ width: '150px' }}>Details</th>
                              <th style={{ width: '320px' }}>Parameter</th>
                              {activePeriods.map((pKey) => (
                                <th key={pKey} className="text-center" style={{ minWidth: '110px' }}>
                                  {formatPeriodHeader(pKey)}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            <tr>
                              <td rowSpan="1" className="font-weight-bold align-middle bg-light">Return Parameters</td>
                              <td>Return in %</td>
                              {activePeriods.map((pKey) => (
                                <td key={pKey} className="text-right font-weight-bold">
                                  {formatPercent(riskParams.return_in_percent?.[pKey])}
                                </td>
                              ))}
                            </tr>
                            <tr>
                              <td rowSpan="4" className="font-weight-bold align-middle bg-light">Risk Parameters</td>
                              <td>Max drawdown or decline from peak %</td>
                              {activePeriods.map((pKey) => (
                                <td key={pKey} className="text-right text-danger font-weight-bold">
                                  {formatPercent(riskParams.max_drawdown_or_decline_from_peak_percent?.[pKey])}
                                </td>
                              ))}
                            </tr>
                            <tr>
                              <td>Standard deviation %</td>
                              {activePeriods.map((pKey) => (
                                <td key={pKey} className="text-right">
                                  {formatPercent(riskParams.standard_deviation?.[pKey])}
                                </td>
                              ))}
                            </tr>
                            <tr>
                              <td>Risk Adjusted Return (Sharpe ratio)</td>
                              {activePeriods.map((pKey) => (
                                <td key={pKey} className="text-right">
                                  {formatPercent(riskParams.sharpe_ratio?.[pKey])}
                                </td>
                              ))}
                            </tr>
                            <tr>
                              <td>No. of days taken to recover from max drawdown or max decline</td>
                              {activePeriods.map((pKey) => (
                                <td key={pKey} className="text-right">
                                  {formatValue(riskParams.no_of_days_to_recover_from_max_drawdown?.[pKey])}
                                </td>
                              ))}
                            </tr>
                          </tbody>
                        </table>
                      </>
                    );
                  })()}

                  {/* 52 Weeks High & Low Index Value Table (PDF Page 14 Table 18) */}
                  {highLow && (
                    <table className="parrva-report-table mb-2">
                      <thead>
                        <tr>
                          <th className="text-center" style={{ width: '50%' }}>
                            52 weeks high hit as on {highLow.high_date || '-'}
                          </th>
                          <th className="text-center" style={{ width: '50%' }}>
                            52 weeks low hit as on {highLow.low_date || '-'}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td className="text-center font-weight-bold text-success" style={{ fontSize: '13px' }}>
                            {highLow.high_value ? parseFloat(highLow.high_value).toFixed(2) : '-'}
                          </td>
                          <td className="text-center font-weight-bold text-danger" style={{ fontSize: '13px' }}>
                            {highLow.low_value ? parseFloat(highLow.low_value).toFixed(2) : '-'}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  )}
                </div>
              );
            })}
          </div>
        )}
        </div>
      </div>
    </div>
  );
};

export default CareparrvaReportDetail;
